/**
 * Base collection model. All Minasa model collections should inherit this class.
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import { Collection } from 'vue-mc';
import BaseModel from './BaseModel';
import { instance as getApi, getTokenSource } from '@/helpers/api';
import { CancelTokenSource } from 'axios';
import { FilterInfo, SortInfo } from '@/@types/api_types';

const globalCancelTokens: { [key: string]: CancelTokenSource } = {};

export default class BaseCollection extends Collection {
    _cancelTokenSrc: CancelTokenSource | null = null;
    _sorters: SortInfo[] | null = null;
    _extraParams: { [key: string]: any } | null = null;
    _filters: { [key: string]: FilterInfo } | null = null;
    loading = false;

    defaults() {
        return {
            // The total record count as delivered by the backend
            remoteTotal: 0,
            apiCancelToken: Math.random().toString(16).substr(2, 12),
        };
    }
    /**
     * Override in child classes: This is the API endpoint's
     * entity name (e.g. like in /api/User/list --> User)
     *
     * @return {String} The API endpoint's Entity name
     */
    entityName() {
        return this.constructor.name;
    }

    /**
     * Creates a new cancel token, cancels an ALREADY issued token for this collection.
     * If the 'apiCancelToken' attribute is set on this collection, a global token
     * will be used, otherwise an instance-only token is used.
     */
    createCancelToken() {
        let tokenSrc: CancelTokenSource | null = null;
        const cancelTokenIdent = this.get('apiCancelToken');

        // Cancel an already running request, if applicable:
        if (cancelTokenIdent && globalCancelTokens[cancelTokenIdent]) {
            tokenSrc = globalCancelTokens[cancelTokenIdent];
        } else {
            tokenSrc = this._cancelTokenSrc;
        }

        if (tokenSrc) {
            tokenSrc.cancel('request has been canceled');
            this.removeCancelToken();
        }

        tokenSrc = getTokenSource();

        // If requested, store a global cancel token for later cancellation:
        if (cancelTokenIdent) {
            globalCancelTokens[cancelTokenIdent] = tokenSrc;
        }
        this._cancelTokenSrc = tokenSrc;
        return tokenSrc.token;
    }

    removeCancelToken() {
        const cancelTokenIdent = this.get('apiCancelToken');
        if (cancelTokenIdent) {
            delete globalCancelTokens[cancelTokenIdent];
        }
        this._cancelTokenSrc = null;
    }

    setRemoteSort(sortObj: SortInfo | SortInfo[]) {
        if (!this._sorters) {
            this._sorters = [];
        }
        if (!(sortObj instanceof Array)) {
            sortObj = [sortObj];
        }
        this._sorters = [...sortObj];
        return this;
    }

    addRemoteSort(sortObj: SortInfo | SortInfo[]) {
        if (!(sortObj instanceof Array)) {
            sortObj = [sortObj];
        }
        return this.setRemoteSort([...(this._sorters || []), ...sortObj]);
    }

    /**
     * Add extra paramters that are sent with every request, e.g. a fixed
     * filter param for an entity.
     */
    addExtraParam(key: string, value: any) {
        if (!this._extraParams) {
            this._extraParams = {};
        }
        this._extraParams[key] = value;
        return this;
    }

    removeExtraParam(key: string) {
        if (!this._extraParams) {
            this._extraParams = {};
        }
        delete this._extraParams[key];
        return this;
    }

    /**
     * Sets a specific filter for the query, which is sent as JSON
     * array of filters (see backend: QueryBuilder::handleFilter)
     * in the form of '[{property:key, operator, value}, ...]'
     */
    setFilter(key: string, value: any, operator = '=') {
        if (!this._filters) {
            this._filters = {};
        }
        this._filters[key] = {
            property: key,
            operator: operator,
            value: value,
        };
        return this;
    }
    removeFilter(key: string) {
        if (!this._filters) {
            this._filters = {};
        }
        delete this._filters[key];
        return this;
    }
    clearFilters() {
        this._filters = {};
    }

    queryUrl() {
        return `/${this.entityName()}/list`;
    }

    destroyUrl() {
        return `/${this.entityName()}/destroy`;
    }

    listDependenciesUrl() {
        return `/${this.entityName()}/listDependencies`;
    }

    /**
     * Simple, generic query function: calls /entity/list API, with
     * the given options as request params.
     *
     * Fills this collection with the returned records.
     *
     * @param {object} options request parameters
     */
    async query(options?: { [key: string]: any } | null) {
        this.loading = true;

        // apply sorters
        if (this._sorters) {
            options = {
                ...{ sort: this._sorters },
                ...options,
            };
        }
        options = { ...options, ...(this._extraParams || {}) };
        if (this._filters) {
            options = {
                ...options,
                ...{ filter: Object.keys(this._filters).map((key) => (this._filters ? this._filters[key] : null)) },
            };
        }

        const cancelToken = this.createCancelToken();
        const response = await getApi().post(this.queryUrl(), options, {
            cancelToken,
        });
        this.removeCancelToken();

        this.replace(response ? response.data.records : null);
        this.set('remoteTotal', response ? response.data.total : null);
        this.loading = false;
        return this;
    }

    async deleteMultiple(
        values: Array<BaseModel | number>,
        options?: { [key: string]: any } | null,
    ): Promise<BaseCollection> {
        if (this.loading) {
            return this; // Ignore the request if the collection is already loading
        }

        this.loading = true;

        let ids: Array<number | string> = [];
        if (values.length > 0 && values[0] instanceof BaseModel) {
            ids = this.getIdentifiers(values as BaseModel[]);
        } else {
            ids = values as number[];
        }
        options = {
            ids,
            ...options,
        };

        const cancelToken = this.createCancelToken();
        await getApi().post(this.destroyUrl(), options, {
            cancelToken,
        });
        this.removeCancelToken();

        // Remove the deleted values from the collection
        let records: BaseModel[] = [];
        if (values.length > 0 && values[0] instanceof BaseModel) {
            records = values as BaseModel[];
        } else {
            records = (values as number[])
                .map((value) => this.find({ id: value }))
                .filter((record): record is BaseModel => record !== undefined);
        }

        this.remove(records);

        this.loading = false;

        return this;
    }

    async listDependencies(
        values: Array<BaseModel | number>,
        options?: { [key: string]: any } | null,
    ): Promise<Array<any>> {
        let ids: Array<number | string> = [];
        if (values.length > 0 && values[0] instanceof BaseModel) {
            ids = this.getIdentifiers(values as BaseModel[]);
        } else {
            ids = values as number[];
        }
        options = {
            ids,
            ...options,
        };

        const cancelToken = this.createCancelToken();
        const response = await getApi().post(this.listDependenciesUrl(), options, {
            cancelToken,
        });
        this.removeCancelToken();

        return response.data.dependencies;
    }
}
